import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, catchError, map, of } from 'rxjs';
import { ModalService, TaModalComponentRef } from 'teccom-web-components';
import { TermsOfUseAcceptance, TermsOfUseApi } from './terms-of-use.api';
import { TermsOfUseDialogComponent, TermsOfUseDialogData } from './terms-of-use-dialog.component';

/**
 * Terms of Use / Privacy Policy acceptance in the Shell (formerly only `UserPrivacyPolicyGuard` in Legacy).
 * Checked once per signed-in user when the session is initialized; a user who has not accepted the current
 * version gets a blocking dialog over whatever page he lands on. Legacy keeps its own check for its pages;
 * both use the same backend, so one acceptance counts for both applications.
 */
@Injectable({ providedIn: 'root' })
export class TermsOfUseService {
  private readonly api = inject(TermsOfUseApi);
  private readonly modal = inject(ModalService);
  private readonly translate = inject(TranslateService);

  private dialog?: TaModalComponentRef<TermsOfUseDialogComponent>;

  /**
   * Completes once the acceptance is known (not once it is given). As in Legacy, a failing check does not
   * block the session: the user is asked again on the next sign-in or in Legacy.
   */
  ensureAccepted(): Observable<void> {
    return this.api.getAcceptance(this.currentLanguage()).pipe(
      map((acceptance: TermsOfUseAcceptance) => {
        if (!acceptance.hasUserAccepted) {
          this.openDialog(acceptance.latestPrivacyPolicy?.content ?? '');
        }
      }),
      catchError(() => of(undefined))
    );
  }

  private openDialog(content: string): void {
    if (this.dialog) {
      return;
    }
    const data: TermsOfUseDialogData = {
      content,
      // The language at the time of the acceptance, like Legacy
      accept: () => this.api.accept(this.currentLanguage())
    };
    this.dialog = this.modal.openComponent(TermsOfUseDialogComponent, data, {
      size: 'l',
      placement: 'center',
      hasBackdrop: true,
      dismissable: false
    });
    this.dialog.afterClosed.subscribe(() => this.dialog = undefined);
  }

  private currentLanguage(): string {
    return this.translate.currentLang || this.translate.defaultLang || 'en';
  }
}
